import { useRef, useEffect, MutableRefObject, useState } from 'react'
import { FRONT_PAYMENT_CODE, FRONT_PAYMENT_METHOD, FRONT_PAYMENT_ACTION_TYPE, BIZ_TRADE_SCENE_TYPE, INIT_PAYMENT_TIMEOUT_DURATION, PAY_COMPONENT_CALLED_TYPE } from '@Pages/member/constant/pay'
import { MONITOR_SIGN, MONITOR_VALUE, MONITOR_CODE } from '@Pages/member/constant/raptor'
import { RECORD_PAYMENT_STEP } from '@Pages/member/constant/logan'
import i18nClient from '@Pages/member/OrderSubmit/i18n.config'
import { post } from '@Pages/member/common/api'
import type { RequestVo } from '@Pages/member/common/api'
import { OrderType } from '@Pages/member/constant'
import Modal from '@/components/Modal'
import { Raptor, Logan, jumpTo, RouterType } from '@/common/utils'
import Pay, { BizType, PayComponentCallbackEvent } from './Pay'
import './index.css'

const indicatorModule = Mach.requireModule('ActivityIndicator')

const payAction2Logan = {
  [FRONT_PAYMENT_ACTION_TYPE.INIT_PAYMENT]: RECORD_PAYMENT_STEP.INIT_PAYMENT_CALLBACK,
  [FRONT_PAYMENT_ACTION_TYPE.UPDATE_PAY_METHOD]: RECORD_PAYMENT_STEP.UPDATE_PAY_METHOD,
  [FRONT_PAYMENT_ACTION_TYPE.PAY_METHOD_CHANGE]: RECORD_PAYMENT_STEP.CHANGE_PAY_METHOD,
  [FRONT_PAYMENT_ACTION_TYPE.START_CASHIER]: RECORD_PAYMENT_STEP.PAYMENT_CALLBACK,
}

const PayComponent = ({
  tradeScene, // 场景标识
  mtConstractId, // 签约id
  orderViewId, // 订单ID 用来取消订单
  payment, // 支付方式 用来Raptor上报
  getRef, // 获取Ref
  getPayMethod, // 获取支付方式
  onInitFail, // 初始化失败方法
  onInitSuccess, // 初始化成功方法
  onPayFail, // 支付失败方法
  onPaySuccess, // 支付成功方法
}: {
  tradeScene: BIZ_TRADE_SCENE_TYPE
  mtConstractId?: string
  orderViewId?: string
  payment: FRONT_PAYMENT_METHOD
  getRef?: (ref: MutableRefObject<null>) => void
  getPayMethod?: (method: FRONT_PAYMENT_METHOD) => void
  onInitFail?: (data?: any) => void
  onInitSuccess?: (data: any) => void
  onPayFail?: (data?: any) => void
  onPaySuccess?: (data: any) => void
}) => {
  const { t } = i18nClient
  // 获取前置收银台初始化超时时间，默认iOS超时时长800毫秒 android超时时长为1000毫秒
  // TODO: 是否通过Horn下发初始化超时时长
  const defaultDuration = Mach.env.isIOS ? INIT_PAYMENT_TIMEOUT_DURATION.IOS : INIT_PAYMENT_TIMEOUT_DURATION.ANDROID
  const paymentRef = useRef(null)
  const payActionRef = useRef('') // 支付组件业务场景实例
  const [initStartTime, setInitStartTime] = useState(0) // 初始化时长
  const [errorMsg, setErrorMsg] = useState('') // 失败文案
  const [visible, setVisible] = useState(false) // 失败弹框
  // init失败通用方法
  const initFailCommonFn = () => {
    setVisible(true)
    // TODO: 是否增加版控提醒
    setErrorMsg(t('keetamember_payment_method_failure_1', '获取支付方式失败，请稍后重试'))
    // TODO: 是否需要回到上一页
  }
  useEffect(() => {
    // 记录init开始时间
    setInitStartTime(Date.now())
    if (getRef) getRef(paymentRef)
    const initPaymentTimeoutId = setTimeout(() => {
      // 有场景标识代表支付组件已加载，不执行超时逻辑
      if (payActionRef?.current) return
      initFailCommonFn()
      const commonTags = {
        // status对应Value值，方便魔术查询
        status: MONITOR_VALUE.FAILED,
        code: MONITOR_CODE.INIT_TIMEOUT,
      }
      // 初始化成功率监控
      Raptor.reportMetrics({
        values: {
          // 前置收银台成功率, 成功是1，失败是0
          [MONITOR_SIGN.INIT_SUCCESS_RATE]: MONITOR_VALUE.FAILED,
        },
        tags: commonTags,
      })
      // 初始化成功监控时长
      Raptor.reportMetrics({
        values: {
          // 前置收银台初始化时长
          [MONITOR_SIGN.INIT_DURATION]: defaultDuration,
        },
        tags: commonTags,
      })
    }, defaultDuration)
    return () => {
      // 取消初始化前置收银组件超时监听
      clearTimeout(initPaymentTimeoutId)
    }
  }, [])

  // raptor上传更换支付方式成功率
  const changePayWayRaptor = ({ isSuccess, code }: { isSuccess: boolean; code: number }) => {
    Raptor.reportMetrics({
      values: {
        // 前置收银台切换支付方式成功率
        [MONITOR_SIGN.CHANGE_PAY_WAY]: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
      },
      tags: {
        // status对应Value值，方便魔术查询
        status: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
        payCode: code,
      },
    })
  }
  // raptor支付成功率
  const paySuccessRaptor = ({ isSuccess, payment }: { isSuccess: boolean; payment: FRONT_PAYMENT_METHOD }) => {
    Raptor.reportMetrics({
      values: {
        // 前置收银台支付成功率, 成功是1，失败是0
        [MONITOR_SIGN.PAY_SUCCESS_RATE]: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
      },
      tags: {
        // status对应Value值，方便魔术查询
        status: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
        payment,
      },
    })
  }
  // 支付失败
  const payFailFn = async (result?: any) => {
    console.log('支付失败', result)
    // 取消订单
    // if (orderViewId) {
    //   const data = await post(
    //     'api/v1/order/virtual/cancelSubmit',
    //     {
    //       orderType: OrderType.BuyMember,
    //       orderViewId
    //     },
    //     {
    //       loading: true,
    //       onError: (error: any, data?: RequestVo) => {
    //         if (data) {
    //           switch (data?.code) {
    //             case 201003412:
    //               // 取消订单失败，订单已支付成功
    //               // TODO: 如何做？
    //               break;
    //             default:
    //               // 取消订单失败,TODO: 如何做？
    //               break;
    //           }
    //         } else {
    //           // 取消订单失败,TODO: 如何做？
    //         }
    //       }
    //     },
    //   )
    // }
    onPayFail && onPayFail(result)
    // Logan日志 TODO: 这里上报取消订单日志？
    // Logan.log({ type: RECORD_PAYMENT_STEP.CANCEL_ORDER, data: { retryCount: 0 } })
  }
  // 支付组件回调
  const handlePaymentCallback = (event: PayComponentCallbackEvent) => {
    const payAction = event?.payAction
    const code = event?.payData?.code ?? -1
    console.log('给业务event', event)
    payActionRef.current = payAction
    // 支付组件回调代码Logan日志上报
    Logan.log({ type: payAction2Logan[payAction], data: event })
    if (payAction === FRONT_PAYMENT_ACTION_TYPE.INIT_PAYMENT) {
      // 记录初始化时长
      const initEndTime = Date.now()
      const duration = initEndTime - initStartTime
      const isSuccess = code === FRONT_PAYMENT_CODE.RENDER_SUCCESS
      if (isSuccess) {
        // 用户可操作
        onInitSuccess && onInitSuccess(event?.payData)
      } else initFailCommonFn()
      // 通知上报时长
      const params = {
        payAction: PAY_COMPONENT_CALLED_TYPE.REPORT_INFO,
        payData: {
          pageStartTimestamp: initStartTime,
          pageEndTimestamp: initEndTime,
          isPageStart: true, // 如果支付组件是和提单页一起渲染，就传true，否则false
        },
      }
      // @ts-ignore
      paymentRef.current?.callPayComponent?.(params)
      // raptor上报
      const tags = {
        // status对应Value值，方便魔术查询
        status: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
        code: isSuccess ? MONITOR_CODE.INIT_SUCCESS : MONITOR_CODE.INIT_FAILED,
      }
      // 初始化成功率监控
      Raptor.reportMetrics({
        values: {
          // 前置收银台成功率, 成功是1，失败是0
          [MONITOR_SIGN.INIT_SUCCESS_RATE]: isSuccess ? MONITOR_VALUE.SUCCESS : MONITOR_VALUE.FAILED,
        },
        tags,
      })
      // 初始化成功监控时长
      Raptor.reportMetrics({
        values: {
          // 前置收银台初始化时长
          [MONITOR_SIGN.INIT_DURATION]: duration,
        },
        tags,
      })
    } else if (payAction === FRONT_PAYMENT_ACTION_TYPE.UPDATE_PAY_METHOD) {
      if (code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_SUCCESS) {
        const payMethod = event?.payData?.payMethod ?? FRONT_PAYMENT_METHOD.NONE
        getPayMethod && getPayMethod(payMethod)
      } else if (code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_UNINSTALL_APP || code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_UNAUTHORIZED) {
        // 业务调用支付组件方法透传当前支付限制方式
        const params = {
          payAction: PAY_COMPONENT_CALLED_TYPE.UPDATE_PAY_METHOD,
          payData: {
            restrictPayMethods: [], // 重置支付方式浮层列表
          },
        }
        // @ts-ignore
        paymentRef.current?.callPayComponent?.(params)
      } else {
        const payMethod = FRONT_PAYMENT_METHOD.NONE
        getPayMethod && getPayMethod(payMethod)
      }
      // raptor上报
      changePayWayRaptor({ isSuccess: code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_SUCCESS, code })
    } else if (payAction === FRONT_PAYMENT_ACTION_TYPE.PAY_METHOD_CHANGE) {
      if (code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_SUCCESS) {
        const payMethod = event?.payData?.payMethod ?? FRONT_PAYMENT_METHOD.NONE
        getPayMethod && getPayMethod(payMethod)
      }
      // raptor上报
      changePayWayRaptor({ isSuccess: code === FRONT_PAYMENT_CODE.UPDATE_PAY_METHOD_SUCCESS, code })
    } else if (payAction === FRONT_PAYMENT_ACTION_TYPE.START_CASHIER) {
      const result = event?.payData
      if (!result || !result.resultData) {
        payFailFn()
        paySuccessRaptor({ isSuccess: false, payment })
        return
      }
      const { code } = JSON.parse(result.resultData)
      // -1 代表有支付结果（成功或失败）
      if (result.resultCode === -1) {
        if (code !== FRONT_PAYMENT_CODE.PAY_SUCCESS) {
          payFailFn(result)
        } else {

          /**
           * 支付成功返回到提单页
           * ios 会有任务栈错乱的问题，所以延迟 800ms 在跳转到订单详情页
           * android 任务栈的顺序，由系统保证，是串行的，不存在任务栈错乱的问题
           */
          onPaySuccess && onPaySuccess(result)
        }
        paySuccessRaptor({ isSuccess: code === FRONT_PAYMENT_CODE.PAY_SUCCESS, payment })
      }
      paySuccessRaptor({ isSuccess: false, payment })
    } else {
      // 兜底回到上一页
      indicatorModule?.showLoading()
      setTimeout(() => {
        indicatorModule?.hideLoading()
        jumpTo({ type: RouterType.BACK })
      }, defaultDuration)
      // 初始化成功率监控
      Raptor.reportMetrics({
        values: {
          // 前置收银台成功率, 成功是1，失败是0
          [MONITOR_SIGN.INIT_SUCCESS_RATE]: MONITOR_VALUE.FAILED,
        },
        tags: {
          // status对应Value值，方便魔术查询
          status: MONITOR_VALUE.FAILED,
          code: MONITOR_CODE.UNKNOW,
          payAction,
          payCode: code,
        },
      })
    }
  }
  return (
    <>
      <Pay
        ref={paymentRef}
        payComponentData={{
          bizType: BizType.SailorPreCashier,
          bizData: {
            // 业务参数
            tradeScene,
          },
        }}
        onPayComponentCallback={handlePaymentCallback}
      />
      <Modal
        visible={visible}
        title={errorMsg}
        confirmText={t('keetamember_know_01', '知道了')}
        content={<view style={{ height: 28 }}></view>}
        onConfirm={() => {
          setVisible(false)
          if (payActionRef?.current === FRONT_PAYMENT_ACTION_TYPE.INIT_PAYMENT) onInitFail && onInitFail()
        }}
      />
    </>
  )
}

export default PayComponent
